import { ErrorOutline } from 'components/icons';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Alert } from 'components/ui/alert';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';

export type LedesVersion = 'LEDES_1998BI_V2' | 'LEDES_XML_2_1';

interface VersionOption {
    id: LedesVersion;
    label: string;
}

interface DownloadLedesForm {
    version: LedesVersion;
}

interface Props {
    onClose: () => void;
    onDownload: (version: LedesVersion) => Promise<unknown>;
}

/** Logs a failed LEDES download and says what failed: the server's message, else the generic one. */
export function ledesDownloadError(error: unknown): string {
    logError(error);
    return error instanceof Error && error.message ? error.message : t('invoices.ledes-download.error');
}

const DownloadLedesModal = ({ onClose, onDownload }: Props): JSX.Element => {
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const { control, handleSubmit, setValue } = useForm<DownloadLedesForm>({
        defaultValues: {
            version: 'LEDES_1998BI_V2',
        },
    });

    const versionOptions: VersionOption[] = [
        { id: 'LEDES_1998BI_V2', label: t('invoices.ledes-download.version-1998bi-v2') },
        { id: 'LEDES_XML_2_1', label: t('invoices.ledes-download.version-xml-2-1') },
    ];

    const onSave = () => {
        handleSubmit(async (data) => {
            setDownloadError(null);
            try {
                await onDownload(data.version);
                onClose();
            } catch (error) {
                setDownloadError(ledesDownloadError(error));
            }
        })();
    };

    return (
        <>
            <ModalHeader title={t('invoices.ledes-download.title')} onCancel={onClose} />
            <div className="p-5">
                <div className="flex flex-col gap-4">
                    <RadioInput
                        name="version"
                        control={control}
                        setValue={setValue}
                        label="invoices.ledes-download.version"
                        options={versionOptions}
                        getOptionIdentifier="id"
                        getOptionLabel="label"
                    />
                    {downloadError !== null && (
                        <Alert className="bg-error-lighter text-fg flex items-center gap-2 rounded-md border-transparent py-2 pr-4 pl-3 font-semibold">
                            <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">
                                <ErrorOutline className="text-error" />
                            </span>
                            <div className="min-w-0">{downloadError}</div>
                        </Alert>
                    )}
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} saveButtonTextId="invoices.ledes-download.download" />
        </>
    );
};

export default DownloadLedesModal;
